import Socketer from '@openticket/socketer';
import { getGlobal, OpenTicketGlobal } from '@/utils/windowOpenTicket';
import { Logger, LogLevel } from '@openticket/lib-shop';
import { ShopEnv, useEnv } from '@/shopenv';

const rawPhases = {
    0: 'Unknown',
    1: 'Arrived',
    2: 'Numbered',
    3: 'Admitted',
    4: 'Finished',
    5: 'Removed',
} as const;
const Phases: { [K in typeof rawPhases[keyof typeof rawPhases]]: K } = {
    Unknown: 'Unknown',
    Arrived: 'Arrived',
    Numbered: 'Numbered',
    Admitted: 'Admitted',
    Finished: 'Finished',
    Removed: 'Removed',
} as const;
type Phase = typeof Phases[keyof typeof Phases];

function isPhaseIndex(v: unknown): v is keyof typeof rawPhases {
    return typeof v === 'number' && v in rawPhases;
}

function toPhase(k: keyof typeof rawPhases): Phase {
    return rawPhases[k];
}

type WaasServiceEventHandler<Evt extends string> = (
    event: Evt,
    cb: (error?: string) => void
) => () => void;

export interface WaasService {
    readonly client: Socketer;
    connectionStartAt: Date;
    connectionOpenAt: Date | null;
    readonly customerId: string;
    currentPhase: Phase;
    lastStatus: string;
    readonly on: WaasServiceEventHandler<'state-change'>;
    readonly queueId: string;
    readonly spot: bigint;
    readonly stateChangeHandlers: Set<(error?: string) => void>;
    readonly token: string | null;
}

export function useWaas(log: Logger): WaasService | null {
    // TODO Implementation notes:
    // - There is currently no way to catch Waas initially failing to register. (initial GET call).
    // - dockerized stop start has a high chance of reaching an unrecoverable state.
    //      - [og] {"level":"fatal","error":"failed to connect for creating keyspace; gocql: unable to create session: unable to discover protocol version: dial tcp :0->172.18.0.27:9042: connect: connection refused","message":"could not connect to scylla scylla"}
    //      - [bs] {"level":"error","traceId":"0f6f7eae101bcf3e","spanId":"1a4194de49305cda","parentId":"0f6f7eae101bcf3e","queue":"5123a0ae-909c-4596-953c-001f6c9ed15d","error":"rpc error: code = Unavailable desc = connection error: desc = \"transport: Error while dialing: dial tcp 172.18.0.17:1200: connect: connection refused\"","message":"could not request number"}
    //      - [vc] {"level":"error","URL":"http://vc.waas.openticket.local/register/5123a0ae-909c-4596-953c-001f6c9ed15d/","method":"GET","queue":"5123a0ae-909c-4596-953c-001f6c9ed15d","customer":"280bdfa0-62ef-5e90-aaf8-dde12e1b6e2e","error":"rpc error: code = Unavailable desc = connection error: desc = \"transport: Error while dialing: dial tcp 172.18.0.17:1200: connect: connection refused\"","message":"new session created"}
    //      - Temporary fix: dockerized stop bs vc og start og bs vc

    const [queueId] = window.location.pathname.replace(/^\/+/, '').split('/');

    const {
        waasCustomerId: customerId,
        waasVisitorContainerHostname: visitorContainerHostname,
    }: ShopEnv = useEnv();

    const OT: OpenTicketGlobal = getGlobal();

    const logData = { customerId, queueId };

    try {
        if (!OT.Waas) {
            const waas: WaasService = {
                client: new Socketer({
                    queueId,
                    customerId,
                    proto: 'wss',
                    host: visitorContainerHostname,
                    secure: true,
                    preCb: [
                        () => {
                            waas.connectionStartAt = new Date();
                            notifyState(waas);
                            sendLogMessage(
                                log,
                                waas,
                                ['waas', 'client', 'connection', 'start'],
                                'Attempting connection to WaaS.',
                                logData
                            );
                        },
                    ],
                    openCb: [
                        () => {
                            waas.connectionOpenAt = new Date();
                            notifyState(waas);
                            sendLogMessage(
                                log,
                                waas,
                                ['waas', 'client', 'connection', 'open'],
                                'Connection to WaaS opened.',
                                logData
                            );
                        },
                    ],
                    success: [
                        () => {
                            waas.lastStatus = 'connected';
                            notifyState(waas);
                            sendLogMessage(
                                log,
                                waas,
                                ['waas', 'client', 'connection', 'success'],
                                'Connection to WaaS sucessfully established.',
                                { ...logData, level: LogLevel.Info }
                            );
                        },
                    ],
                    errorCb: [
                        (evt: unknown) => {
                            waas.lastStatus = 'error';
                            notifyState(waas, evt);
                            sendLogMessage(
                                log,
                                waas,
                                ['waas', 'client', 'connection', 'error'],
                                'Waas connection error.',
                                {
                                    ...logData,
                                    error: evt,
                                    level: LogLevel.Error,
                                }
                            );
                        },
                    ],
                    closeCb: [
                        (evt: unknown) => {
                            waas.lastStatus = 'disconnected';
                            console.error(evt);
                            notifyState(waas, evt);

                            sendLogMessage(
                                log,
                                waas,
                                ['waas', 'client', 'connection', 'close'],
                                'Waas connection closed.',
                                {
                                    ...logData,
                                    error: evt,
                                    level: LogLevel.Error,
                                }
                            );
                        },
                    ],
                    place: [
                        () => {
                            notifyState(waas);

                            // Not sending log messages for fear of excessive log spam once implemented.
                            // However, note that this callback is never triggered in the current socketer.
                            sendLogMessage(
                                log,
                                waas,
                                ['waas', 'client', 'place', 'change'],
                                'Waas place changed.',
                                { ...logData, level: LogLevel.None }
                            );
                        },
                    ],
                    phase: [
                        (ph: unknown) => {
                            if (isPhaseIndex(ph)) {
                                waas.currentPhase = toPhase(ph);

                                notifyState(waas);
                                sendLogMessage(
                                    log,
                                    waas,
                                    ['waas', 'client', 'phase', 'change'],
                                    'Waas phase changed.',
                                    { ...logData, level: LogLevel.Info }
                                );

                                return;
                            }

                            let error = `ph <type: ${typeof ph}>`;
                            if (
                                typeof ph === 'number' ||
                                typeof ph === 'string'
                            ) {
                                error += ` <value: ${ph}>`;
                            }

                            notifyState(waas, error);

                            sendLogMessage(
                                log,
                                waas,
                                ['waas', 'client', 'phase', 'invalid'],
                                'Waas phase not recognized.',
                                { ...logData, error, level: LogLevel.Error }
                            );
                        },
                    ],
                }),
                connectionStartAt: new Date(),
                connectionOpenAt: null,
                currentPhase: Phases.Unknown,
                customerId,
                lastStatus: 'idle',
                on(
                    event: 'state-change',
                    cb: (error?: string) => void
                ): () => void {
                    switch (event) {
                        case 'state-change':
                            waas.stateChangeHandlers.add(cb);

                            return () => {
                                waas.stateChangeHandlers.delete(cb);
                            };
                        default:
                            throw new Error(
                                `Failed to register event handler. Invalid event ${event} for waas service`
                            );
                    }
                },
                queueId, // Note, this is not necesarily the current queueId, only the one requested.
                get spot(): bigint {
                    // socketer#spot() has BigInt as a return type (the constructor).
                    // bigint (the primitive) should be used instead, workaround by using valueOf.
                    return waas.client.spot().valueOf();
                },
                stateChangeHandlers: new Set(),
                get token(): string | null {
                    return waas.client.token();
                },
            };

            OT.Waas = waas;
        }
    } catch (error) {
        sendLogMessage(
            log,
            OT.Waas,
            ['waas', 'client', 'init', 'failed'],
            'Failed to initialise WaaS.',
            { ...logData, error, level: LogLevel.Fatal }
        );

        return null;
    }

    return OT.Waas;
}

function notifyState(waas: WaasService, error?: unknown): void {
    let errorMsg: string | undefined;

    try {
        if (error) {
            if (typeof error === 'string') {
                errorMsg = error;
            } else if (isCloseEvent(error)) {
                errorMsg = 'CloseEvent:';
                errorMsg += `[code]: ${error.code.toFixed(0)} | `;
                errorMsg += `[reason]: ${error.reason} | `;
                errorMsg += `[clean]: ${error.wasClean.toString()}`;
            } else if (
                typeof error === 'object' &&
                'toString' in error &&
                typeof error.toString === 'function'
            ) {
                errorMsg = error.toString();
            }
        }
    } catch {
        errorMsg = 'Non serializable error';
    }

    for (const handler of waas.stateChangeHandlers) {
        try {
            handler(errorMsg);
        } catch (e) {
            console.error('[WAAS] Failed to call state change handler', e);
        }
    }
}

function sendLogMessage(
    log: Logger,
    waas: WaasService | undefined,
    path: [string, ...string[]],
    message: string,
    {
        customerId,
        queueId,
        error,
        level,
    }: {
        customerId: string;
        queueId: string;

        error?: unknown;
        level?: LogLevel;
    }
): void {
    const data = compileLogData(waas, path, {
        customerId,
        error,
        queueId,
    });

    if (level !== LogLevel.None) {
        log.create(level || LogLevel.Debug, path, data, message);
    }

    console.groupCollapsed('[WAAS] ' + message);
    try {
        console.log(path);
        console.log('Level', error ? LogLevel.Error : LogLevel.Debug);
        console.table(data);
    } finally {
        console.groupEnd();
    }
}

function compileLogData(
    waas: WaasService | undefined,
    path: [string, ...string[]],
    {
        customerId,
        error,
        queueId,
    }: {
        customerId: string;
        error?: unknown;
        queueId: string;
    }
): Record<string, string> {
    const data: Record<string, string> = {
        waas_queue_id: queueId,
        waas_customer_id: customerId,
        waas_token: waas?.client.token() || '',
        waas_spot: waas?.client.spot().toString(10) || '',
        waas_connection_start_at:
            waas?.connectionStartAt.getTime().toFixed(0) || '',
        waas_connection_open_at:
            waas?.connectionOpenAt?.getTime().toFixed(0) || '',
        waas_connection_ms_since_start: waas
            ? (Date.now() - waas?.connectionStartAt.getTime()).toFixed(0)
            : '',
        waas_last_status: waas?.lastStatus || '',
        waas_current_phase: waas?.currentPhase || '',
    };

    try {
        if (error && typeof error === 'string') {
            data.waas_error = error;
        } else if (isCloseEvent(error)) {
            data.waas_error = 'CloseEvent';
            data.waas_error_code = error.code.toFixed(0);
            data.waas_error_reason = error.reason;
            data.waas_error_clean = error.wasClean.toString();
        } else if (
            error &&
            typeof error === 'object' &&
            'toString' in error &&
            typeof error.toString === 'function'
        ) {
            data.waas_error = error.toString();
        }
    } catch {
        data.waas_error = 'Non serializable error';
    }

    if (path.length === 4 && path.join('.') === 'waas.client.connection.open') {
        // Do not spam this key/value for anything other than the connection.open event.
        data.waas_connecting_duration_ms = waas?.connectionOpenAt
            ? (
                  waas.connectionOpenAt.getTime() -
                  waas.connectionStartAt.getTime()
              ).toFixed(0)
            : '';
    }

    return data;
}

function isCloseEvent(v: unknown): v is CloseEvent {
    return (
        !!v &&
        typeof v === 'object' &&
        !Array.isArray(v) &&
        'code' in v &&
        typeof v.code === 'number' &&
        'reason' in v &&
        typeof v.reason === 'string' &&
        'wasClean' in v &&
        typeof v.wasClean === 'boolean'
    );
}
